import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Text } from '@reservamos/elements';
import PaymentImage from 'ui/atoms/PaymentImage';
import PricingRow from '../../../ui/atoms/PricingRow';

/**
 * Renders a plain text detail row.
 * @param {object} props - The row props.
 * @param {string} props.label - The row label.
 * @param {string} props.value - The row value.
 * @returns {JSX.Element} The rendered detail row.
 */
const DetailsRow = ({ label, value }) => (
  <div className="grid items-center grid-flow-col grid-cols-2">
    <Text size="S" color="grayLight">
      {`${label} `}
    </Text>
    <Text size="S" color="grayLight" textAlign="right">
      {value}
    </Text>
  </div>
);

/**
 * Renders the payment details for an Evertec transaction.
 * This component displays a list of payment details including the transaction reference,
 * amount, date, and status. Each detail is presented in a row with its respective label
 * and value. The transaction date is formatted in DD-MM-YYYY format. The status is
 * translated based on its value using the i18n translation files.
 *
 * @param {Object} props - The component props.
 * @param {string} props.transactionDate - The date of the transaction.
 * @param {string} props.status - The payment status.
 * @param {string} props.reference - The purchase token.
 * @param {number} props.amount - The payment amount.
 * @returns {React.Component} A React component that displays the Evertec payment details.
 */
const EvertecPaymentDetails = ({ transactionDate, status, reference, amount }) => {
  const { t } = useTranslation('payment');
  const formattedTransactionDate = (() => {
    if (!transactionDate) return null;

    const parsedDate = moment(transactionDate);
    return parsedDate.isValid() ? parsedDate.format('DD-MM-YYYY') : null;
  })();
  const statusLabel = status ? t(`evertec_payment_status.${status}`) : null;

  return (
    <div className="purchases-section-side">
      <Text weight="bold">{t('evertec_payment_details.title')}</Text>
      <div className="purchase-sidebar-pricing">
        <div className="purchase-pricing">
          <div className="section-content list-style">
            {Boolean(reference) && (
              <DetailsRow label={t('evertec_payment_details.reference')} value={reference} />
            )}
            {Boolean(amount) && (
              <PricingRow label={t('evertec_payment_details.amount')} price={amount} />
            )}
            {Boolean(formattedTransactionDate) && (
              <DetailsRow
                label={t('evertec_payment_details.transaction_date')}
                value={formattedTransactionDate}
              />
            )}
            {Boolean(statusLabel) && (
              <DetailsRow label={t('evertec_payment_details.status')} value={statusLabel} />
            )}
          </div>
        </div>
      </div>
      <PaymentImage type="evertec" />
    </div>
  );
};

DetailsRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

EvertecPaymentDetails.propTypes = {
  transactionDate: PropTypes.string,
  status: PropTypes.string,
  reference: PropTypes.string,
  amount: PropTypes.number,
};

EvertecPaymentDetails.defaultProps = {
  transactionDate: null,
  status: null,
  reference: null,
  amount: null,
};

export default EvertecPaymentDetails;
